import React from 'react';
import formatMoney from '@/lib/formatMoney';
import Badge from '../UI/Badge';

const PositionPanel = ({ positions, closedToday = [] }) => {
  const calculatePnLPercent = (position) => {
    const entryPrice = parseFloat(position.entry_price);
    const currentPrice = parseFloat(position.current_price || position.entry_price);
    return ((currentPrice - entryPrice) / entryPrice) * 100;
  };

  const formatCurrency = (value) => formatMoney(value, { abs: true });

  const formatPercent = (value) => {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(2)}%`;
  };

  const totalUnrealized = positions.reduce((sum, p) => sum + parseFloat(p.unrealized_pnl || 0), 0);
  const totalRealized = positions.reduce((sum, p) => sum + parseFloat(p.realized_pnl || 0), 0)
    + closedToday.reduce((sum, p) => sum + parseFloat(p.realized_pnl || 0), 0);
  const totalPnL = totalUnrealized + totalRealized;

  return (
    <div className="flex flex-col h-full min-h-0 overflow-auto">
      <table className="w-full text-xs sm:text-sm">
        <thead className="sticky top-0 z-10 bg-card">
          <tr className="border-b border-border">
            <th className="text-left py-1.5 px-1.5 sm:px-3 text-muted-foreground font-medium">Symbol</th>
            <th className="text-right py-1.5 px-1.5 sm:px-3 text-muted-foreground font-medium">Qty</th>
            <th className="text-right py-1.5 px-1.5 sm:px-3 text-muted-foreground font-medium">Avg Cost</th>
            <th className="text-right py-1.5 px-1.5 sm:px-3 text-muted-foreground font-medium">Current</th>
            <th className="text-right py-1.5 px-1.5 sm:px-3 text-muted-foreground font-medium">Unrealized</th>
            <th className="text-right py-1.5 px-1.5 sm:px-3 text-muted-foreground font-medium hidden sm:table-cell">Realized</th>
            <th className="text-right py-1.5 px-1.5 sm:px-3 text-muted-foreground font-medium">P&L %</th>
            <th className="text-center py-1.5 px-1.5 sm:px-3 text-muted-foreground font-medium hidden sm:table-cell">Status</th>
          </tr>
        </thead>
        <tbody>
            {positions.map((position) => {
              const unrealized = parseFloat(position.unrealized_pnl || 0);
              const realized = parseFloat(position.realized_pnl || 0);
              const pnlPercent = calculatePnLPercent(position);
              const isProfitable = unrealized >= 0;

              return (
                <tr key={position.id} className="border-b border-border/50 hover:bg-accent/10">
                  <td className="py-1.5 px-1.5 sm:px-3">
                    <span className="font-bold text-foreground">{position.symbol}</span>
                  </td>
                  <td className="text-right py-1.5 px-1.5 sm:px-3 text-muted-foreground">
                    {parseFloat(position.quantity).toFixed(6).replace(/\.?0+$/, '')}
                  </td>
                  <td className="text-right py-1.5 px-1.5 sm:px-3 text-muted-foreground">
                    {formatCurrency(position.entry_price)}
                  </td>
                  <td className="text-right py-1.5 px-1.5 sm:px-3">
                    <div className="flex items-center justify-end gap-1">
                      <span className={isProfitable ? 'text-green-500' : 'text-red-500'}>
                        {isProfitable ? '\u2191' : '\u2193'}
                      </span>
                      <span className="text-foreground">
                        {formatCurrency(position.current_price || position.entry_price)}
                      </span>
                    </div>
                  </td>
                  <td className="text-right py-1.5 px-1.5 sm:px-3">
                    <span className={`font-bold ${isProfitable ? 'text-green-500' : 'text-red-500'}`}>
                      {formatMoney(unrealized, { sign: true })}
                    </span>
                  </td>
                  <td className="text-right py-1.5 px-1.5 sm:px-3 hidden sm:table-cell">
                    <span className={`font-medium ${realized >= 0 ? 'text-green-500/80' : 'text-red-500/80'}`}>
                      {realized !== 0 ? formatMoney(realized, { sign: true }) : '-'}
                    </span>
                  </td>
                  <td className="text-right py-1.5 px-1.5 sm:px-3">
                    <Badge variant={isProfitable ? 'success' : 'danger'}>
                      {formatPercent(pnlPercent)}
                    </Badge>
                  </td>
                  <td className="text-center py-1.5 px-1.5 sm:px-3 hidden sm:table-cell">
                    <Badge variant={position.status === 'open' ? 'info' : 'neutral'}>
                      {position.status || 'open'}
                    </Badge>
                  </td>
                </tr>
              );
            })}
            {closedToday.map((position) => {
              const realized = parseFloat(position.realized_pnl || 0);
              if (realized === 0) return null;
              return (
                <tr key={position.id} className="border-b border-border/50 opacity-60">
                  <td className="py-1.5 px-1.5 sm:px-3">
                    <span className="font-bold text-muted-foreground">{position.symbol}</span>
                  </td>
                  <td className="text-right py-1.5 px-1.5 sm:px-3 text-muted-foreground">0</td>
                  <td className="text-right py-1.5 px-1.5 sm:px-3 text-muted-foreground">{formatCurrency(position.entry_price)}</td>
                  <td className="text-right py-1.5 px-1.5 sm:px-3 text-muted-foreground">-</td>
                  <td className="text-right py-1.5 px-1.5 sm:px-3 text-muted-foreground">-</td>
                  <td className="text-right py-1.5 px-1.5 sm:px-3 hidden sm:table-cell">
                    <span className={`font-medium ${realized >= 0 ? 'text-green-500/80' : 'text-red-500/80'}`}>
                      {formatMoney(realized, { sign: true })}
                    </span>
                  </td>
                  <td className="text-right py-1.5 px-1.5 sm:px-3">-</td>
                  <td className="text-center py-1.5 px-1.5 sm:px-3 hidden sm:table-cell">
                    <Badge variant="neutral">closed</Badge>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
};

export default PositionPanel;
